/**
 * Typed Kai-API client.
 *
 * Responsibilities:
 *   1. Build URLs against `appConfig.apiBaseUrl`.
 *   2. Attach optional `x-api-key` and `x-tenant` headers.
 *   3. Optionally acquire an MSAL Bearer token (when `appConfig.apiScope` is
 *      set) and attach it as `Authorization`.
 *   4. Unwrap the Kai-API `{ success, data, error, timestamp }` envelope and
 *      throw a structured `ApiError` on failure.
 *
 * Designed to be the only place that talks to `fetch`. Higher-level modules
 * (src/api/dashboard.ts, hooks under src/hooks/api/*) compose on top of this.
 */

import { InteractionRequiredAuthError } from '@azure/msal-browser';

import { msalInstance } from '@/auth/msalConfig';
import { appConfig } from '@/config/appConfig';

export interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  timestamp?: string;
}

export class ApiError extends Error {
  status: number;
  body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface RequestOptions {
  /** Query string params. Falsy values are omitted. */
  query?: Record<string, string | number | boolean | undefined | null>;
  /** HTTP method, defaults to GET. */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Optional JSON body. */
  body?: unknown;
  /** Per-request abort signal. */
  signal?: AbortSignal;
  /** Override the configured tenant header for a single request. */
  tenant?: string;
}

/**
 * Acquire an MSAL access token for the configured API scope.
 *
 * Returns `null` when no scope is configured (the API doesn't require auth in
 * local/dev) or when there's no active account yet. Falls back to a redirect
 * on `InteractionRequiredAuthError` so the user is bounced through Entra.
 */
async function acquireApiToken(): Promise<string | null> {
  if (!appConfig.apiScope) return null;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return null;

  try {
    const result = await msalInstance.acquireTokenSilent({
      account,
      scopes: [appConfig.apiScope],
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      // The cached session never consented to the API scope (e.g. it predates
      // adding the scope to loginRequest). Silent acquisition cannot prompt, so
      // bounce the user through an interactive redirect to consent once; the
      // token is cached on return. This navigates away, so nothing returns here.
      console.warn('[api] interactive auth required for API scope; redirecting to consent');
      await msalInstance.acquireTokenRedirect({ account, scopes: [appConfig.apiScope] });
      return null;
    }
    console.error('[api] token acquisition failed', err);
    return null;
  }
}

const buildUrl = (path: string, query?: RequestOptions['query']): string => {
  const base = appConfig.apiBaseUrl.replace(/\/+$/, '');
  const trimmed = path.startsWith('/') ? path : `/${path}`;
  const url = new URL(`${base}${trimmed}`);

  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
};

/**
 * Low-level request helper. Throws `ApiError` on non-2xx OR on
 * `{ success: false, error }`. Returns the unwrapped `data` payload.
 */
export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query);
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (appConfig.apiKey) headers['x-api-key'] = appConfig.apiKey;

  const tenant = opts.tenant ?? appConfig.apiTenant;
  if (tenant) headers['x-tenant'] = tenant;

  const token = await acquireApiToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: opts.method ?? 'GET',
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
      credentials: 'omit',
    });
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err;
    // The URL may carry `provider=<npi>`; that is the signed-in provider's
    // own identifier, not PHI, so it is fine in console / LogRocket capture.
    throw new ApiError(
      `Network error contacting Kai-API at ${url}`,
      0,
      err instanceof Error ? err.message : err,
    );
  }

  // Empty body (204) just resolves to undefined — callers expecting T should
  // explicitly type the call site as `void` or `unknown` in that case.
  if (response.status === 204) {
    return undefined as unknown as T;
  }

  let parsed: ApiEnvelope<T> | T | undefined;
  const text = await response.text();
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text) as ApiEnvelope<T> | T;
    } catch {
      // Not JSON — leave parsed undefined and fall through to error handling.
    }
  }

  if (!response.ok) {
    const message =
      (parsed &&
        typeof parsed === 'object' &&
        'error' in parsed &&
        (parsed as ApiEnvelope<T>).error) ||
      `Kai-API request failed with ${response.status}`;
    throw new ApiError(String(message), response.status, parsed ?? text);
  }

  // Standard Kai-API envelope: { success, data, error, timestamp }. If the
  // route is unwrapped (some legacy ones are), pass the body straight through.
  if (parsed && typeof parsed === 'object' && 'success' in parsed) {
    const envelope = parsed as ApiEnvelope<T>;
    if (!envelope.success) {
      throw new ApiError(envelope.error ?? 'Kai-API reported failure', response.status, envelope);
    }
    return envelope.data as T;
  }
  return parsed as T;
}

/** Convenience GET. */
export const apiGet = <T>(path: string, opts: Omit<RequestOptions, 'method' | 'body'> = {}) =>
  request<T>(path, { ...opts, method: 'GET' });

/** Convenience POST. */
export const apiPost = <T>(
  path: string,
  body: unknown,
  opts: Omit<RequestOptions, 'method'> = {},
) => request<T>(path, { ...opts, method: 'POST', body });
